import { ApolloError, useMutation, useQuery } from "@apollo/client";
import { Alert, Autocomplete, Stack, TextField } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { FacilityWasteContainersQuery } from "generated-graphql/graphql";
import { useState } from "react";

import { FACILITY_WASTE_CONTAINERS_QUERY } from "../Containers/api";
import { CONTAINER_STATUS_LABELS } from "../Containers/containerDisplay";
import { SET_CONTAINER_EPISODIC_EVENT_MUTATION } from "./api";

type ContainerOption = NonNullable<
  NonNullable<FacilityWasteContainersQuery["facility"]>["wasteContainers"]
>["items"][number];

/** Generous, non-paginated pull of the facility's containers for the picker
 * — this is a stub with no search box, so there's no follow-up page to fetch. */
const CONTAINER_OPTIONS_PAGE_SIZE = 500;

function containerOptionLabel(option: ContainerOption): string {
  const area = option.accumulationArea?.name ?? "No area";
  return `${option.containerLabel} · ${CONTAINER_STATUS_LABELS[option.status]} · ${area}`;
}

/** BAD_USER_INPUT errors (satellite area, pre-event waste, inactive
 * container, lapsed event) surface their server message verbatim; anything
 * else falls back to a generic toast. Exported for unit testing. */
export function describeAttachError(error: unknown, fallback: string): string {
  if (!(error instanceof ApolloError)) {
    return fallback;
  }
  const badUserInput = error.graphQLErrors.find((e) => e.extensions?.code === "BAD_USER_INPUT");
  return badUserInput?.message ?? fallback;
}

export type AttachContainerDialogProps = {
  eventId: string;
  facilityId: string;
  /** Containers already on the event — attaching them again would no-op, so
   * they don't appear as options. */
  excludeContainerIds: string[];
  open: boolean;
  onClose: () => void;
};

/**
 * An Autocomplete over the facility's containers, then a single mutation
 * call. Server-side rejections are shown verbatim and leave the dialog open
 * so the user can pick another container.
 */
export function AttachContainerDialog({
  eventId,
  facilityId,
  excludeContainerIds,
  open,
  onClose,
}: AttachContainerDialogProps) {
  const alerts = useAlerts();
  const [selectedContainer, setSelectedContainer] = useState<ContainerOption | null>(null);

  const { data, loading: loadingOptions } = useQuery(FACILITY_WASTE_CONTAINERS_QUERY, {
    variables: { facilityId, pageSize: CONTAINER_OPTIONS_PAGE_SIZE },
    skip: !open,
    fetchPolicy: "cache-and-network",
  });
  const attachedIds = new Set(excludeContainerIds);
  const containerOptions: ContainerOption[] = (data?.facility?.wasteContainers?.items ?? []).filter(
    (container) => !attachedIds.has(container.id)
  );

  const [setContainerEpisodicEvent, { loading: attaching }] = useMutation(
    SET_CONTAINER_EPISODIC_EVENT_MUTATION,
    { refetchQueries: ["FacilityEpisodicEvents", "FacilityAccumulationSummary"] }
  );

  const handleClose = () => {
    setSelectedContainer(null);
    onClose();
  };

  const handleAttach = async () => {
    if (!selectedContainer) {
      return;
    }
    await setContainerEpisodicEvent({
      variables: { containerId: selectedContainer.id, episodicEventId: eventId },
      onCompleted: () => {
        alerts.success("Container attached to event");
        handleClose();
      },
      onError: (error) => {
        alerts.error(describeAttachError(error, "An error occurred while attaching the container"));
      },
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      title="Attach container"
      primaryActionButtonProps={{
        loading: attaching,
        onClick: handleAttach,
        disabled: !selectedContainer,
        saveText: "Attach",
      }}
    >
      <Stack spacing={2}>
        <Alert severity="info">
          Attached containers clock to the event&apos;s 60-day removal deadline. A container that
          already holds waste can only join if that waste was generated during the event. Empty
          containers can always be attached.
        </Alert>
        <Autocomplete
          options={containerOptions}
          loading={loadingOptions}
          getOptionLabel={containerOptionLabel}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedContainer}
          onChange={(_event, value) => setSelectedContainer(value)}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {containerOptionLabel(option)}
            </li>
          )}
          renderInput={(params) => <TextField {...params} label="Container" autoFocus />}
        />
      </Stack>
    </Dialog>
  );
}
